import i18n from 'helpers/i18n';
import messages from './messages';
import { useRef } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'ramda';
import type { KeyboardEvent } from 'react';

import { ACTIVE, COMPLETED, CANCELED } from 'constants/trainingStatus';
import {
  getTrainingsStatusResultsPanelId,
  getTrainingStatusTabId
} from '../utils';

type StatusFilterParams = {
  /** The current value for the status selected. */
  value: string;
  /** Callback fired when the status changes. */
  onChange: (name: string) => void;
  /** Shared accessibility id used to link tabs with results panel. */
  a11yId: string;
};

const TABS = [
  { name: 'all', message: messages.all },
  { name: ACTIVE, message: messages.active },
  { name: COMPLETED, message: messages.completed },
  { name: CANCELED, message: messages.canceled }
];

function StatusFilter({ value, onChange, a11yId }: StatusFilterParams) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedName = isEmpty(value) ? 'all' : value;
  const statusResultsPanelId = getTrainingsStatusResultsPanelId(a11yId);

  const setTabRef =
    (index: number) =>
    (element: HTMLButtonElement | null): void => {
      tabRefs.current[index] = element;
    };

  function handleClick(name: string) {
    onChange(name === 'all' ? '' : name);
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      return;
    }

    event.preventDefault();

    let nextIndex = index;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % TABS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
      default:
        break;
    }

    tabRefs.current[nextIndex]?.focus();
    handleClick(TABS[nextIndex].name);
  }

  return (
    <div className="mb-4 font-inter">
      <div className="font-medium text-ds-xs pb-2 text-ds-text-secondary">
        {i18n.ft(messages.status)}
      </div>
      <div
        role="tablist"
        aria-label={i18n.ft(messages.status)}
        className="inline-flex items-center rounded-md bg-ds-cool-gray-3 p-0.5"
      >
        {TABS.map(({ name, message }, index) => {
          const isSelected = selectedName === name;
          const tabId = getTrainingStatusTabId(a11yId, name);

          return (
            <button
              key={name}
              ref={setTabRef(index)}
              id={tabId}
              type="button"
              role="tab"
              aria-selected={isSelected}
              aria-controls={statusResultsPanelId}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => handleClick(name)}
              onKeyDown={event => handleKeyDown(event, index)}
              className={clsx(
                'h-8 min-w-[88px] rounded-[6px] px-4 text-sm font-inter font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-1',
                isSelected
                  ? 'border border-ds-border bg-ds-white text-ds-black shadow-ds-resting'
                  : 'border border-transparent bg-transparent text-ds-text-secondary hover:bg-ds-cool-gray-2 hover:text-ds-black'
              )}
            >
              {i18n.ft(message)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default StatusFilter;
